/* eslint-disable no-param-reassign */
import { useCallback, type MutableRefObject, type ComponentType } from 'react';
import { Grid } from 'react-flexbox-grid';
import { useIntl } from 'react-intl';
import pick from 'lodash/pick';
import useFormValuesRef from 'hooks/useFormValuesRef';
import useBudgetCalculator from 'hooks/budget/useBudgetCalculator';
import PageTitle from 'components/common/PageTitle';
import client from 'graphql/apollo';
import StepWizard from 'components/common/StepWizard';
import Nav from 'components/calculators/Nav';
import { isUndefined } from 'lodash';
import {
  MyBudgetDocument,
  type MyBudgetQuery,
  type MyBudgetQueryVariables,
  AloneOrPartner,
  BudgetCalculatorImportance,
  BudgetCalculatorProjectType,
} from 'graphql/generated/graphql';
import { messageOf } from 'utils/error';
import requireValue from 'utils/requireValue';
import FirstStep from './LifestyleCalculatorFirstStep';
import SecondStep from './LifestyleCalculatorSecondStep';
import ThirdStep from './LifestyleCalculatorThirdStep';
import FourthStep from './LifestyleCalculatorFourthStep';
import FifthStep from './LifestyleCalculatorFifthStep';
import SixthStep from './LifestyleCalculatorSixthStep';
import FinalStep from './LifestyleCalculatorFinalStep';
import type {
  ILifestyleFormValues,
  ILifestyleStepProps,
  ISubmitStepOptions,
  TFormDataKey,
  TMonthlyExpenseKey,
  TMyBudget,
  TSubmitBudgetCalculator,
} from './types';
import {
  getSubmitStep,
  toBudgetCalculatorInput,
  toNumberValueKey,
  type TMonthlyExpensesCategories,
} from './utils';

const formValueMock: ILifestyleFormValues = {
  alone_or_partner: AloneOrPartner.partner,
  amount_of_alimony: 0,
  cars: 1,
  kids_quantity: 2,
  monthly_net_salary: 6000,
  monthly_net_salary_partner: 2000,
  monthly_loan: 100,
  other_revenues: 1000,
  own_funds: 200000,
  square_meters: 160,
  project_type: BudgetCalculatorProjectType.buying,
  buying_with_agent: true,
  new_car: BudgetCalculatorImportance.important,
  luxuries: BudgetCalculatorImportance.important,
  hobbies: BudgetCalculatorImportance.not_important,
  socializing: BudgetCalculatorImportance.not_important,
  pension_and_insurances: BudgetCalculatorImportance.not_important,
  technology: BudgetCalculatorImportance.very_important,
  taking_a_break: BudgetCalculatorImportance.very_important,
  age: 25,
};

const formDataAllowedKeys: TFormDataKey[] = [
  'alone_or_partner',
  'kids_quantity',
  'monthly_net_salary',
  'monthly_net_salary_partner',
  'other_revenues',
  'cars',
  'buying_with_agent',
  'own_funds',
  'amount_of_alimony',
  'monthly_loan',
  'project_type',
  'square_meters',
  'broker_commission',
  'property_transfer_tax',
  'land_register_fee',
  'notary_fee',
  'age',
];

const monthlyExpensesKeys: TMonthlyExpenseKey[] = [
  'socializing',
  'luxuries',
  'new_car',
  'taking_a_break',
  'hobbies',
  'technology',
  'pension_and_insurances',
];

const useSubmitBudgetCalculator = (
  formValuesRef: MutableRefObject<ILifestyleFormValues>,
  calculatorCompleted: boolean | undefined,
  budgetStep: number | null | undefined,
) => {
  const budgetCalculatorSubmit = useBudgetCalculator();

  const onSubmit: TSubmitBudgetCalculator = useCallback(
    async (
      values: ILifestyleFormValues,
      { setSubmitting, setErrors }: Parameters<TSubmitBudgetCalculator>[1],
      { currentStep, cacheStep }: ISubmitStepOptions,
    ) => {
      formValuesRef.current = { ...formValuesRef.current, ...values };
      setSubmitting(true);

      try {
        const allowedFormValues = pick(formValuesRef.current, formDataAllowedKeys);
        const monthlyExpensesFields = pick(formValuesRef.current, monthlyExpensesKeys);
        const { project_type, buying_with_agent } = formValuesRef.current;

        const additionalExpenses: TFormDataKey[] = ['broker_commission', 'property_transfer_tax'];
        Object.keys(pick(allowedFormValues, additionalExpenses)).forEach((valueKey) => {
          if (
            valueKey === 'broker_commission' &&
            (project_type !== BudgetCalculatorProjectType.buying || !buying_with_agent)
          ) {
            allowedFormValues.broker_commission = null;
          }
          if (
            valueKey === 'property_transfer_tax' &&
            (project_type === BudgetCalculatorProjectType.renovation ||
              project_type === BudgetCalculatorProjectType.building)
          ) {
            allowedFormValues.property_transfer_tax = null;
          }
        });

        // The keys `pick` found, in its order.
        const monthlyExpensesCategories = monthlyExpensesKeys
          .filter((key) => key in monthlyExpensesFields)
          .reduce<TMonthlyExpensesCategories>((acc, categoryName) => {
            const numberValue = formValuesRef.current[toNumberValueKey(categoryName)] ?? null;
            const value = { importanceValue: monthlyExpensesFields[categoryName], numberValue };
            return { ...acc, [categoryName]: value };
          }, {});

        const _step = getSubmitStep(currentStep, budgetStep, calculatorCompleted);

        const budget = await budgetCalculatorSubmit(
          toBudgetCalculatorInput(allowedFormValues, monthlyExpensesCategories),
          _step,
        );

        if (!cacheStep) window.scrollTo(0, 0);

        const step = !isUndefined(cacheStep) ? cacheStep : _step;
        client.writeQuery<MyBudgetQuery, MyBudgetQueryVariables>({
          query: MyBudgetDocument,
          data: {
            myBudget: {
              // The API returns the estimation with the result (a null one would write only `step`).
              ...requireValue(budget.budgetEstimation, 'budgetEstimation'),
              step: cacheStep || step,
            },
          },
        });
      } catch (error) {
        setErrors({ form: messageOf(error) });
      } finally {
        setSubmitting(false);
      }
    },
    [formValuesRef, budgetCalculatorSubmit, calculatorCompleted, budgetStep],
  );
  return onSubmit;
};

interface ITab {
  label: string;
  component: ComponentType<ILifestyleStepProps>;
}

interface ILifestyleCalculatorFormProps {
  initialValues: ILifestyleFormValues;
  initialStep?: number;
  myBudget?: TMyBudget | null;
  calculatorCompleted?: boolean;
}

const tabs: ITab[] = [
  { label: 'budget_calculator.first_step_title', component: FirstStep },
  { label: 'budget_calculator.second_step_title', component: SecondStep },
  { label: 'budget_calculator.third_step_title', component: ThirdStep },
  { label: 'budget_calculator.fourth_step_title', component: FourthStep },
  { label: 'budget_calculator.fifth_step_title', component: FifthStep },
  { label: 'budget_calculator.sixth_step_title', component: SixthStep },
  { label: 'budget_calculator.final_step_title', component: FinalStep },
];

const LifestyleCalculatorForm = ({
  initialValues,
  initialStep,
  myBudget,
  calculatorCompleted,
}: ILifestyleCalculatorFormProps) => {
  const { formatMessage: t } = useIntl();
  const formValuesRef = useFormValuesRef([], { formValueMock, initialValues });
  const onSubmit = useSubmitBudgetCalculator(formValuesRef, calculatorCompleted, myBudget?.step);

  const onSubmitStep = useCallback(
    (values: ILifestyleFormValues) => {
      window.scrollTo(0, 0);
      formValuesRef.current = { ...formValuesRef.current, ...values };
    },
    [formValuesRef],
  );

  const onBack = useCallback(
    (values: ILifestyleFormValues, callback?: () => void) => {
      onSubmitStep(values);
      requireValue(callback, 'callback')();
    },
    [onSubmitStep],
  );

  return (
    <Grid>
      <PageTitle>{t({ id: 'page_titles.budget' })}</PageTitle>

      <StepWizard
        initialStep={initialStep}
        nav={
          <Nav
            {...{
              tabs,
              isEditMode: calculatorCompleted,
              clickableTabStep: 6,
              step: myBudget?.step,
            }}
          />
        }
      >
        {tabs.map(({ label, component: C }) => (
          <C
            key={label}
            {...{
              initialValues,
              clickableTabStep: 6,
              myBudget,
              formValuesRef,
              onSubmitStep,
              onSubmit,
              onBack,
              calculatorCompleted,
            }}
          />
        ))}
      </StepWizard>
    </Grid>
  );
};

export default LifestyleCalculatorForm;
